import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { addChecklistItem, createChecklistFromTemplates, getDealMembersForAssignment } from '../../api/checklist-queries';
import { getDocumentsByDeal } from '../../api/queries_v2';
import { useNotifications } from '../../notifications';
import LoadingSpinner from '../loading-spinner';
import styles from './AddItemForm.module.scss';

const selectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
    borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(33, 150, 243, 0.1)' : null,
    '&:hover': {
      borderColor: state.isFocused ? '#2196f3' : 'lightgray',
    },
  }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: '200px',
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  singleValue: (provided: any) => ({
    ...provided,
    fontSize: '14px',
  }),
  placeholder: (provided: any) => ({
    ...provided,
    fontSize: '14px',
    color: '#999',
  }),
};

interface AddItemFormProps {
  dealId: string;
  checklists: Array<{ id: string }>;
  onSuccess: () => void;
  onCancel: () => void;
  refetch: () => Promise<any>;
}

export const AddItemForm: React.FC<AddItemFormProps> = ({ dealId, checklists, onSuccess, onCancel, refetch }) => {
  const { formatMessage } = useIntl();
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  const [content, setContent] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedToId, setAssignedToId] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [autoCompleteOnSignature, setAutoCompleteOnSignature] = useState(false);
  const [sendReminder, setSendReminder] = useState(false);
  const [reminderDaysBefore, setReminderDaysBefore] = useState(1);

  const [createChecklist, { loading: creating }] = useMutation(createChecklistFromTemplates);
  const [addItem, { loading: adding }] = useMutation(addChecklistItem);

  // Fetch deal members for assignment
  const { data: membersData, loading: membersLoading } = useQuery(getDealMembersForAssignment, {
    variables: { dealId },
    skip: !dealId,
  });

  // Fetch deal documents
  const { data: documentsData, loading: documentsLoading } = useQuery(getDocumentsByDeal, {
    variables: { dealId },
    skip: !dealId,
  });

  const members = membersData?.dealMembersForAssignment || [];
  const documents = documentsData?.getDocumentsByDeal || [];

  // Filter out signed documents
  const unsignedDocuments = documents.filter((doc: any) =>
    !doc.signedDocumentUrl && !doc.isExecuted
  );

  // Build sorted options for react-select
  const memberOptions = useMemo(() =>
    [...members]
      .sort((a: any, b: any) => (a.fullName || '').localeCompare(b.fullName || ''))
      .map((m: any) => ({ value: m.id, label: m.fullName })),
    [members]
  );

  const documentOptions = useMemo(() =>
    [...unsignedDocuments]
      .sort((a: any, b: any) => (a.title || '').localeCompare(b.title || ''))
      .map((d: any) => ({ value: d.id, label: d.title })),
    [unsignedDocuments]
  );

  const isSubmitting = creating || adding;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!content.trim()) {
      notifyError({
        message: {
          id: 'checklist.item.content.required',
          defaultMessage: 'Please enter item content',
        },
      });
      return;
    }

    try {
      let checklistId: string;

      // Ensure checklist exists
      if (checklists.length === 0) {
        const result = await createChecklist({
          variables: {
            dealId,
            name: 'Checklist',
            templateIds: [],
          },
        });

        if (result.data?.createChecklistFromTemplates?.errors?.length) {
          notifyError({
            message: {
              id: 'checklist.create.error',
              defaultMessage: result.data.createChecklistFromTemplates.errors[0] || 'Failed to create checklist',
            },
          });
          return;
        }

        await refetch();
        checklistId = result.data?.createChecklistFromTemplates?.dealChecklist?.id;
      } else {
        checklistId = checklists[0].id;
      }

      // Add the item
      const itemResult = await addItem({
        variables: {
          checklistId,
          content: content.trim(),
          dueDate: dueDate || undefined,
          assignedToId: assignedToId || undefined,
          documentId: documentId || undefined,
          autoCompleteOnSignature: autoCompleteOnSignature || undefined,
          reminderDaysBefore: (sendReminder && dueDate) ? reminderDaysBefore : undefined,
        },
      });

      if (itemResult.data?.addChecklistItem?.errors?.length) {
        notifyError({
          message: {
            id: 'checklist.item.add.error',
            defaultMessage: itemResult.data.addChecklistItem.errors[0] || 'Failed to add item',
          },
        });
        return;
      }

      notifySuccess({
        message: {
          id: 'checklist.item.add.success',
          defaultMessage: 'Item added successfully',
        },
      });

      await refetch();
      onSuccess();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist.item.add.error',
          defaultMessage: 'Failed to add item',
        },
      });
    }
  };

  if (membersLoading || documentsLoading) {
    return (
      <div className={styles.addItemForm__loading}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.addItemForm}>
      <Form onSubmit={handleSubmit} className={styles.addItemForm__form}>
        {/* Item Content */}
        <Form.Group className={styles.addItemForm__field}>
          <Form.Label>
            <FormattedMessage id="checklist.item.content" defaultMessage="Item" />
            <span className={styles.addItemForm__required}>*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder={formatMessage({
              id: 'checklist.item.content.placeholder',
              defaultMessage: 'e.g., Review property inspection report',
            })}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            className={styles.addItemForm__textarea}
          />
        </Form.Group>

        {/* Linked Document */}
        <Form.Group className={styles.addItemForm__field}>
          <Form.Label>
            <FormattedMessage id="checklist.item.linkedDocument" defaultMessage="Linked Document (Optional)" />
          </Form.Label>
          <Select
            options={documentOptions}
            value={documentOptions.find((o: any) => o.value === documentId) || null}
            onChange={(option: any) => setDocumentId(option ? option.value : '')}
            isClearable
            isDisabled={isSubmitting}
            placeholder={formatMessage({ id: 'checklist.item.linkedDocument.none', defaultMessage: 'No document linked' })}
            styles={selectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
          />
          <Form.Text className="text-muted">
            <FormattedMessage
              id="checklist.item.linkedDocument.help"
              defaultMessage="Only unsigned documents are shown"
            />
          </Form.Text>
        </Form.Group>

        {/* Assigned To + Due Date side by side */}
        <div className={styles.addItemForm__dueDateRow}>
          <Form.Group className={styles.addItemForm__dueDateField}>
            <Form.Label>
              <FormattedMessage id="checklist.item.assignedTo" defaultMessage="Assigned To (Optional)" />
            </Form.Label>
            <Select
              options={memberOptions}
              value={memberOptions.find((o: any) => o.value === assignedToId) || null}
              onChange={(option: any) => setAssignedToId(option ? option.value : '')}
              isClearable
              isDisabled={isSubmitting}
              placeholder={formatMessage({ id: 'checklist.item.assignedTo.none', defaultMessage: 'No one assigned' })}
              styles={selectStyles}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
            />
          </Form.Group>

          <Form.Group className={styles.addItemForm__dueDateField}>
            <Form.Label>
              <FormattedMessage id="checklist.item.dueDate" defaultMessage="Due Date (Optional)" />
            </Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Form.Group>
        </div>

        {/* Send Reminder */}
        {dueDate && (
          <Form.Group className={styles.addItemForm__checkboxField}>
            <Form.Check
              type="checkbox"
              id="send-reminder"
              label={formatMessage({
                id: 'checklist.item.sendReminder',
                defaultMessage: 'Send reminder before due date',
              })}
              checked={sendReminder}
              onChange={(e) => setSendReminder(e.target.checked)}
            />
            {sendReminder && (
              <div className={styles.addItemForm__reminderDays}>
                <Form.Label>
                  <FormattedMessage id="checklist.item.reminderDaysBefore" defaultMessage="Days before:" />
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="30"
                  value={reminderDaysBefore}
                  onChange={(e) => setReminderDaysBefore(parseInt(e.target.value) || 1)}
                  className={styles.addItemForm__reminderInput}
                />
              </div>
            )}
          </Form.Group>
        )}

        {/* Auto-complete on signature */}
        {documentId && (
          <Form.Group className={styles.addItemForm__checkboxField}>
            <Form.Check
              type="checkbox"
              id="auto-complete-signature"
              label={formatMessage({
                id: 'checklist.item.autoComplete',
                defaultMessage: 'Auto-complete when linked document is signed',
              })}
              checked={autoCompleteOnSignature}
              onChange={(e) => setAutoCompleteOnSignature(e.target.checked)}
            />
          </Form.Group>
        )}

        {/* Action Buttons */}
        <div className={styles.addItemForm__actions}>
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            <FormattedMessage id="checklist.item.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting || !content.trim()}>
            {isSubmitting ? (
              <FormattedMessage id="checklist.item.adding" defaultMessage="Adding..." />
            ) : (
              <FormattedMessage id="checklist.item.add" defaultMessage="Add Item" />
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddItemForm;
